import type { StateCreator } from 'zustand';
import type { Card } from '../../types';
import type { StoreState } from '../useStore';
import * as dataApi from '../../services/dataApi';
import { normalizeCard } from '../normalizers';
import { checkCardMilestones } from '@/services/milestones';
import { reportError, trackBreadcrumb } from '@/services/errorTracking';
import { isOfflinePersistenceError, queueOfflineCardUpdate } from '@/utils/offlineSync';
import type { PersistenceReceipt } from '@/utils/offlineSync';

// A card counts as "reviewed" only when it has both an SRS scheduling state
// and a last-review timestamp. Using this single predicate in BOTH queues
// keeps getDueCards and getNewCards provably disjoint: a card is either new
// or due, never both. (Without this, addCard seeds dueDate=now and srsState=null,
// which previously placed fresh cards in both lists.)
const isReviewed = (card: Card) => !!card.srsState && !!card.lastReviewed;

export interface CardsSlice {
  cards: Card[];
  setCards: (cards: Card[]) => void;
  addCard: (card: Omit<Card, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateCard: (id: string, updates: Partial<Card>) => Promise<PersistenceReceipt>;
  swapCardImage: (
    cardId: string,
    newImageUrl: string,
    source?: string,
    provenance?: Card['imageProvenance'],
  ) => Promise<void>;
  /** Flag a card whose stored photo failed to load (dead upstream); excluded from review until repaired. */
  markCardImageBroken: (cardId: string) => Promise<void>;
  deleteCard: (id: string) => Promise<void>;
  getDueCards: (deckId?: string) => Card[];
  getNewCards: (deckId?: string, limit?: number) => Card[];
}

export const createCardsSlice: StateCreator<StoreState, [], [], CardsSlice> = (set, get) => ({
  cards: [],
  setCards: (cards) => set({ cards }),

  addCard: async (card) => {
    try {
      const res = (await dataApi.createCard({
        ...card,
        cardOrigin: 'learner',
        dueDate: card.dueDate ?? new Date().toISOString(),
      })) as Record<string, unknown>;
      const newCard = normalizeCard(res);
      set((state) => ({
        cards: [...state.cards, newCard],
        decks: state.decks.map((d) =>
          d.id === card.deckId ? { ...d, cardCount: d.cardCount + 1 } : d,
        ),
      }));
      // Fire-and-forget milestone check
      const state = get();
      const langPair = state.settings.currentLanguagePair;
      const pairDeckIds = new Set(
        state.decks.filter((d) => d.languagePair === langPair).map((d) => d.id),
      );
      const newCount = state.cards.filter(
        (c) => c.cardOrigin === 'learner' && pairDeckIds.has(c.deckId),
      ).length;
      if (newCard.cardOrigin === 'learner') checkCardMilestones(newCount);
    } catch (err) {
      reportError(err, { operation: 'addCard' });
      throw err;
    }
  },

  updateCard: async (id, updates) => {
    const previous = get().cards.find((c) => c.id === id);
    if (previous) {
      // Review sessions need SRS changes to be visible immediately so the
      // in-memory due queue cannot repeat a card while persistence is pending.
      set((state) => ({
        cards: state.cards.map((c) => (c.id === id ? { ...c, ...updates } : c)),
      }));
    }

    try {
      const res = (await dataApi.updateCard(id, updates)) as Record<string, unknown>;
      const updated = normalizeCard(res);
      set((state) => ({
        cards: state.cards.map((c) => (c.id === id ? updated : c)),
      }));
      return 'persisted';
    } catch (err) {
      reportError(err, { operation: 'updateCard' });
      if (isOfflinePersistenceError(err)) {
        queueOfflineCardUpdate({ cardId: id, updates: updates as Record<string, unknown> });
        // Expected success-path when offline; not reportError (Vidi-02m).
        trackBreadcrumb('offline', 'Card update queued for offline sync');
        return 'queued';
      }
      if (previous) {
        set((state) => ({
          cards: state.cards.map((c) => (c.id === id ? previous : c)),
        }));
      }
      throw err;
    }
  },

  swapCardImage: async (cardId, newImageUrl, source, provenance) => {
    const previous = get().cards.find((c) => c.id === cardId);
    if (!previous) return;

    // Preserve FSRS memory: swapping a photo is a personal-connection edit,
    // not a memory reset. The word and its scheduling history stay intact.
    const nextCard: Card = {
      ...previous,
      imageUrl: newImageUrl,
      imageSource: source ?? previous.imageSource,
      imageProvenance: provenance ?? null,
    };

    // Optimistic update: patch the card locally so the review UI advances
    // before we round-trip the DB.
    set((state) => ({
      cards: state.cards.map((c) => (c.id === cardId ? nextCard : c)),
    }));

    try {
      // Re-sync from the DB response: dataApi mirrors external URLs on write,
      // so the persisted image_url can differ from the raw pick. Adopting the
      // response keeps the store pointing at the stable mirrored asset.
      const res = (await dataApi.updateCard(cardId, {
        imageUrl: newImageUrl,
        ...(source ? { imageSource: source } : {}),
        imageProvenance: provenance ?? null,
      })) as Record<string, unknown>;
      const updated = normalizeCard(res);
      set((state) => ({
        cards: state.cards.map((c) => (c.id === cardId ? updated : c)),
      }));
    } catch (err) {
      // Roll back to the pre-swap state. The user sees the old image again
      // and the rating flow continues as if they hadn't swapped.
      set((state) => ({
        cards: state.cards.map((c) => (c.id === cardId ? previous : c)),
      }));
      reportError(err, { operation: 'swapCardImage', extra: { cardId } });
      throw err;
    }
  },

  markCardImageBroken: async (cardId) => {
    const previous = get().cards.find((c) => c.id === cardId);
    // Already-flagged: nothing to do — avoids a write per render of a broken
    // image (ProgressiveImage fires onImageError once per src, but the same
    // card can error in Library AND review within one session).
    if (!previous || previous.imageBrokenAt) return;

    const flaggedAt = new Date().toISOString();
    // Optimistic: the card leaves the due/new queues immediately so the
    // session that detected the dead image is the last one to see it.
    set((state) => ({
      cards: state.cards.map((c) => (c.id === cardId ? { ...c, imageBrokenAt: flaggedAt } : c)),
    }));

    try {
      await dataApi.updateCard(cardId, { imageBrokenAt: flaggedAt });
    } catch (err) {
      // Roll back: worst case the card shows its broken placeholder again
      // next session and gets re-flagged then (or by the nightly sweep).
      set((state) => ({
        cards: state.cards.map((c) => (c.id === cardId ? previous : c)),
      }));
      reportError(err, { operation: 'markCardImageBroken', extra: { cardId } });
    }
  },

  deleteCard: async (id) => {
    const cardToDelete = get().cards.find((c) => c.id === id);
    try {
      await dataApi.deleteCard(id);
      set((state) => ({
        cards: state.cards.filter((c) => c.id !== id),
        decks: state.decks.map((d) =>
          d.id === cardToDelete?.deckId ? { ...d, cardCount: Math.max(0, d.cardCount - 1) } : d,
        ),
      }));
    } catch (err) {
      reportError(err, { operation: 'deleteCard' });
      throw err;
    }
  },

  getDueCards: (deckId?) => {
    const now = new Date().toISOString();
    const state = get();
    const pairDeckIds = new Set(
      state.decks
        .filter((d) => d.languagePair === state.settings.currentLanguagePair)
        .map((d) => d.id),
    );
    return state.cards.filter((card) => {
      const matchesDeck = !deckId || card.deckId === deckId;
      const inCurrentPair = pairDeckIds.has(card.deckId);
      const isDue = card.dueDate <= now;
      return (
        matchesDeck &&
        inCurrentPair &&
        isDue &&
        isReviewed(card) &&
        card.status !== 'suspended' &&
        // Photo-choice law: image-less cards never enter review. A draft
        // (Create batch / word-pack Skip) waits for its photo in Library.
        card.imageUrl !== '' &&
        // Broken-photo cards sit out until repaired: reviewing a word without
        // its image defeats the dual-coding mechanism the app is built on.
        !card.imageBrokenAt
      );
    });
  },

  getNewCards: (deckId?, limit = 20) => {
    const state = get();
    const pairDeckIds = new Set(
      state.decks
        .filter((d) => d.languagePair === state.settings.currentLanguagePair)
        .map((d) => d.id),
    );
    return state.cards
      .filter((card) => {
        const matchesDeck = !deckId || card.deckId === deckId;
        const inCurrentPair = pairDeckIds.has(card.deckId);
        const isNew = !isReviewed(card);
        return (
          matchesDeck &&
          inCurrentPair &&
          isNew &&
          card.status !== 'suspended' &&
          // Photo-choice law: image-less drafts (Create batch / word-pack
          // Skip) are NOT new cards — they wait for their photo in Library.
          card.imageUrl !== '' &&
          !card.imageBrokenAt
        );
      })
      .slice(0, limit);
  },
});
